import { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, CheckCircle, RefreshCw, MessageSquare, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PickupScreen = () => {
  const [preorders, setPreorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending' or 'all'
  const [pickupTarget, setPickupTarget] = useState(null); // { id, name, phone }

  useEffect(() => {
    fetchPreorders();
  }, []);

  const fetchPreorders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/preorders`);
      setPreorders(response.data);
    } catch (error) {
      console.error('Error fetching preorders:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmPickedUp = async () => {
    if (!pickupTarget) return;
    const { id, name } = pickupTarget;
    setPickupTarget(null);
    try {
      await axios.patch(`${API}/preorders/${id}`, { status: 'fulfilled' });
      await fetchPreorders();
      toast.success(`Picked up · ${name || 'Customer'}`, {
        description:
          '✅ Done. Next: in Lightspeed, finalize the sale and apply the preorder deposit to complete the transaction.',
        duration: 8000,
      });
    } catch (error) {
      console.error('Error updating preorder:', error);
      toast.error('Failed to update order — please try again.');
    }
  };

  const handleRevertPickup = async (preorderId, customerName) => {
    // First confirmation
    const firstConfirm = window.confirm(
      `⚠️ UNDO PICKUP\n\n` +
      `Customer: ${customerName || 'Unknown'}\n\n` +
      `Are you sure you want to revert this order back to pending?\n\n` +
      `This action will mark the order as NOT picked up.`
    );
    
    if (!firstConfirm) {
      return;
    }
    
    // Second confirmation - more explicit
    const secondConfirm = window.confirm(
      `⚠️ FINAL CONFIRMATION REQUIRED\n\n` +
      `Please confirm you want to UNDO the pickup for:\n` +
      `${customerName || 'this customer'}\n\n` +
      `Click OK to revert to PENDING\n` +
      `Click Cancel to keep as PICKED UP`
    );
    
    if (!secondConfirm) {
      return;
    }
    
    try {
      await axios.patch(`${API}/preorders/${preorderId}/revert`);
      await fetchPreorders();
      alert('✅ Order reverted to pending status');
    } catch (error) {
      console.error('Error reverting preorder:', error);
      alert('❌ Failed to revert order. Please try again.');
    }
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleText = (phone) => {
    // Opens default SMS app with pre-filled number
    const message = encodeURIComponent('Your order is ready for pickup at our store!');
    window.location.href = `sms:${phone}?body=${message}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.price || 0) * item.qty, 0);
  };

  const filteredPreorders = preorders.filter(p => 
    filter === 'all' ? true : p.status === 'pending'
  );

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <p className="text-center text-gray-500">Loading preorders...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Manrope, sans-serif'}}>Customer Pickup Report</h2>
            <p className="text-gray-600 mt-1">
              {filteredPreorders.length} {filter === 'pending' ? 'pending' : 'total'} preorder{filteredPreorders.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="filter-pending"
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                filter === 'pending'
                  ? 'bg-[#0066FF] text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Pending Only
            </button>
            <button
              data-testid="filter-all"
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                filter === 'all'
                  ? 'bg-[#0066FF] text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              All Orders
            </button>
            <button
              data-testid="refresh-pickup-button"
              onClick={fetchPreorders}
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-semibold rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Preorders List */}
      {filteredPreorders.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <p className="text-center text-gray-500 py-8">
            {filter === 'pending' ? 'No pending preorders' : 'No preorders found'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredPreorders.map((preorder, index) => (
            <div
              key={preorder.id}
              data-testid={`pickup-card-${index}`}
              className={`bg-white border-2 rounded-xl p-5 transition-all ${
                preorder.status === 'fulfilled'
                  ? 'border-green-200 bg-green-50/30'
                  : 'border-gray-200 hover:border-[#0066FF]/30'
              }`}
            >
              {/* Customer Info */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {preorder.order_number ? (
                      <span
                        data-testid={`order-number-${index}`}
                        className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-[#0066FF] bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5"
                      >
                        #{preorder.order_number}
                      </span>
                    ) : null}
                    {preorder.created_at ? (
                      <span
                        data-testid={`order-date-${index}`}
                        className="text-[11px] font-medium text-gray-500"
                      >
                        {formatDate(preorder.created_at)}
                      </span>
                    ) : null}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {preorder.name || 'Customer'}
                  </h3>
                  <p className="text-gray-600 font-medium">{formatPhone(preorder.phone)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {preorder.status === 'fulfilled' ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                      <CheckCircle size={14} />
                      Picked Up
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-[#0066FF] rounded-full text-xs font-semibold">
                      Ready
                    </span>
                  )}
                </div>
              </div>

              {/* Items Summary */}
              <div className="mb-4 bg-gray-50 rounded-lg p-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Items ({preorder.items.length})</h4>
                <div className="space-y-2">
                  {preorder.items.slice(0, 3).map((item, itemIndex) => (
                    <div key={itemIndex} className="flex justify-between gap-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-700 truncate">{item.description}</div>
                        {item.upc ? (
                          <div
                            data-testid={`pickup-upc-${index}-${itemIndex}`}
                            className="font-mono text-[11px] text-gray-500 mt-0.5"
                            title="UPC"
                          >
                            UPC {item.upc}
                          </div>
                        ) : null}
                      </div>
                      <span className="font-semibold text-gray-900 whitespace-nowrap">×{item.qty} ${((item.price || 0) * item.qty).toFixed(2)}</span>
                    </div>
                  ))}
                  {preorder.items.length > 3 && (
                    <p className="text-xs text-gray-500 mt-1">+{preorder.items.length - 3} more items</p>
                  )}
                </div>
                <div className="border-t border-gray-200 mt-3 pt-3 flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-900">Order Total:</span>
                  <span className="text-lg font-bold text-[#0066FF]" style={{fontFamily: 'Manrope, sans-serif'}}>
                    ${calculateTotal(preorder.items).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              {preorder.status === 'pending' ? (
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <button
                      data-testid={`call-button-${index}`}
                      onClick={() => handleCall(preorder.phone)}
                      className="flex-1 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-semibold rounded-lg h-12 px-4 transition-colors flex items-center justify-center gap-2"
                    >
                      <Phone size={18} />
                      {formatPhone(preorder.phone)}
                    </button>
                    <button
                      data-testid={`text-button-${index}`}
                      onClick={() => handleText(preorder.phone)}
                      className="flex-1 bg-blue-50 hover:bg-blue-100 text-[#0066FF] border border-blue-200 font-semibold rounded-lg h-12 px-4 transition-colors flex items-center justify-center gap-2"
                      title="Send text message"
                    >
                      <MessageSquare size={18} />
                      Text
                    </button>
                  </div>
                  <button
                    data-testid={`mark-picked-up-${index}`}
                    onClick={() => setPickupTarget({
                      id: preorder.id,
                      name: preorder.name,
                      orderNumber: preorder.order_number,
                    })}
                    className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-semibold rounded-lg h-12 px-4 transition-colors flex items-center justify-center gap-2"
                  >
                    <CheckCircle size={20} />
                    Picked Up
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    data-testid={`revert-button-${index}`}
                    onClick={() => handleRevertPickup(preorder.id, preorder.name)}
                    className="flex-1 bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 font-semibold rounded-lg h-12 px-4 transition-colors flex items-center justify-center gap-2"
                    title="Undo pickup - requires double confirmation"
                  >
                    <RotateCcw size={18} />
                    Undo Pickup
                  </button>
                  <button
                    data-testid={`call-fulfilled-${index}`}
                    onClick={() => handleCall(preorder.phone)}
                    className="flex-1 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-semibold rounded-lg h-12 px-4 transition-colors flex items-center justify-center gap-2"
                  >
                    <Phone size={18} />
                    {formatPhone(preorder.phone)}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pickupTarget}
        title="Mark as picked up?"
        message={
          pickupTarget ? (
            <span>
              Confirm <strong>{pickupTarget.name || 'this customer'}</strong>
              {pickupTarget.orderNumber ? ` (#${pickupTarget.orderNumber})` : ''} has
              collected their order in store.
            </span>
          ) : null
        }
        hint="Open Lightspeed, scan the items, generate the sale and apply the preorder deposit to close out the transaction."
        confirmLabel="Yes, picked up"
        cancelLabel="Not yet"
        accent="success"
        onConfirm={confirmPickedUp}
        onClose={() => setPickupTarget(null)}
      />
    </div>
  );
};

export default PickupScreen;